import {Box, Stack, Typography} from '@mui/material';
import ArrButton from '../../../../../common/arr-button/ArrButton';
import {ReactComponent as InfoIcon} from './../../../../../../assets/images/info-icon.svg';
import {t} from 'i18next';
import dayjs, {ConfigType} from 'dayjs';
import utc from 'dayjs/plugin/utc';
import './GenerateArtifacts.scss';
import ArrTooltip from '../../../../../common/arr-tooltip/ArrTooltip';
import {useEffect, useState} from 'react';
import GenerateArtifactsModal from './generateArtifactsModal/GenerateArtifactsModal';
import {useReviewHeaderContext} from '../../../header/ReviewHeader';
import ArrLoadingSpinner from '../../../../../common/arr-loading-spinner/ArrLoadingSpinner';
import {ArrToggle} from '../../../../../common/arr-toggle/ArrToggle';
import {
  useCreateProtocolIntelligence,
  useUpdateProtocolIntelligenceDetails,
} from '../../../../../../hooks/ai/review/useReview';
import {ToastType} from '../../../../../../shared/constants';
import {IUpdateProtocolIntelligenceDetailsPayload} from '../../../../../../api/types/aiReview.type';

dayjs.extend(utc);

export const formatUtcTimestamp = (date?: ConfigType) =>
  dayjs(date).utc().format('YYYY/MM/DD HH:mm:ss [UTC]');

const GenerateArtifacts = () => {
  const {
    handleShowReviewAndApproveOpen,
    isArtifactsGenerated,
    isFileSubmitted,
    setIsFileSubmitted,
    isArifactReviewed,
    setIsEditingProtocol,
    setIsApproved,
    isApproved,
    fileName,
    lastModifiedAt,
    uploadedAt,
    isProtocolActive,
    setIsProtocolActive,
    uploadedFile,
    setReviewHeaderToast,
    refetchProtocolData,
    refetchProtocolIds,
    protocolDetails,
  } = useReviewHeaderContext();

  const protocolId = protocolDetails?.protocolId ?? '';

  const {mutate: postProtocolIntelligence} = useCreateProtocolIntelligence();
  const {mutate: updateProtocolIntelligenceDetails} =
    useUpdateProtocolIntelligenceDetails();

  const [showGenerateArtifactsModal, setShowGenerateArtifactsModal] =
    useState<boolean>(false);

  const handleGenerateArtifactsOpen = () => {
    setShowGenerateArtifactsModal(true);
  };

  const handleArtifactsGenerateModalClose = () => {
    setShowGenerateArtifactsModal(false);
    setIsApproved(false);
  };

  const submitFileToGenerateArtifacts = () => {
    const payload = {file: uploadedFile};
    postProtocolIntelligence(payload, {
      onSuccess: () => {
        setIsFileSubmitted(true);
        setReviewHeaderToast({
          isOpen: true,
          message: t(
            'ai.review.protocolIntelligence.submittedFileToastSuccess'
          ),
          status: ToastType.Success,
        });
        refetchProtocolIds();
      },
      onError: (error) => {
        setReviewHeaderToast({
          isOpen: true,
          message:
            error?.response?.data?.detail ??
            t('ai.review.protocolIntelligence.submittedFileToastFailure'),
          status: ToastType.Error,
        });
      },
    });
  };

  const handleReviewArtifacts = () => {
    handleShowReviewAndApproveOpen();
  };
  const handleActiveChange = () => {
    if (!protocolId) return;
    const payload: IUpdateProtocolIntelligenceDetailsPayload = {
      protocolId,
      isActive: !isProtocolActive,
      caseContext: protocolDetails?.caseContext || '',
      relevanceCriteria: protocolDetails?.relevanceCriteria || '',
      issueCodes: protocolDetails?.issueCodes || [],
    };
    updateProtocolIntelligenceDetails(payload, {
      onSuccess: () => {
        setIsProtocolActive((prev) => !prev);
        setReviewHeaderToast({
          isOpen: true,
          message: t(
            'ai.review.protocolIntelligence.isProtocolActiveUpdateSuccess'
          ),
          status: ToastType.Success,
        });
        refetchProtocolData();
      },
      onError: (error) => {
        setReviewHeaderToast({
          isOpen: true,
          message:
            error?.response?.data?.detail ??
            t('ai.review.protocolIntelligence.isProtocolActiveUpdateFailure'),
          status: ToastType.Error,
        });
      },
    });
  };

  // Functional flow validators
  // Step1
  const displayGenerateArtifactsButton =
    !isFileSubmitted && !isArtifactsGenerated && !isArifactReviewed;
  // Step2
  const isFileSubmittedAndArtifactsNotGenerated =
    isFileSubmitted && !isArtifactsGenerated && !isArifactReviewed;
  // Step3
  const isApprovalPending =
    !isArifactReviewed && isArtifactsGenerated && isFileSubmitted;
  // Step4
  const isReviewedAndApproved =
    isArifactReviewed && isArtifactsGenerated && isFileSubmitted;

  useEffect(() => {
    if (isApproved) {
      setIsProtocolActive(true);
    }
  }, [isApproved]);

  const handleFileNameClick = () => {
    if (isReviewedAndApproved) {
      setIsEditingProtocol(true);
    }
  };

  return (
    <>
      <Stack className="file-information" spacing={2}>
        <Stack
          flexDirection={'row'}
          justifyContent={'space-between'}
          alignItems={'flex-start'}
        >
          <Stack gap={1}>
            <ArrTooltip
              data-testid={`tooltip-uploaded-file-protocolIntelligence`}
              content={fileName || ''}
              labelId={`tooltip-uploaded-file-${fileName}`}
            >
              <Box
                className={
                  isReviewedAndApproved
                    ? 'upload-approved'
                    : 'uploaded-file-name'
                }
                data-testid={`file-name-text-approve-${fileName}`}
                onClick={handleFileNameClick}
                aria-label={fileName || 'no-file-name'}
              >
                {fileName || 'no-file-name'}
              </Box>
            </ArrTooltip>
            {isReviewedAndApproved && (
              <Box
                className="upload-moment-description"
                aria-label={`${t('ai.review.protocolIntelligence.updated')} ${formatUtcTimestamp(lastModifiedAt)}`}
                tabIndex={0}
              >
                {t('ai.review.protocolIntelligence.modified')}{' '}
                {formatUtcTimestamp(lastModifiedAt)}
              </Box>
            )}
            {fileName && uploadedAt && (
              <Box
                className="upload-moment-description"
                aria-label={`${t('ai.review.protocolIntelligence.uploaded')} ${formatUtcTimestamp(uploadedAt)}`}
                tabIndex={0}
              >
                {t('ai.review.protocolIntelligence.uploaded')}{' '}
                {formatUtcTimestamp(uploadedAt)}
              </Box>
            )}
          </Stack>
          <Box alignContent={'center'}>
            {displayGenerateArtifactsButton && (
              <ArrButton
                size="small"
                aria-label={t(
                  'ai.review.protocolIntelligence.generateArtifacts'
                )}
                tabIndex={0}
                onClick={handleGenerateArtifactsOpen}
                endIcon={
                  <InfoIcon
                    className="generate-artifacts-info-icon"
                    fontSize="medium"
                    aria-label="information"
                    data-testid="generate-artifacts-info-icon"
                    tabIndex={0}
                  />
                }
                dataTestId="generate-artifacts-generate-artifacts-button"
              >
                {t('ai.review.protocolIntelligence.generateArtifacts')}
              </ArrButton>
            )}
            {isFileSubmittedAndArtifactsNotGenerated && (
              <Stack direction={'row'} alignItems={'flex-end'}>
                <Box>
                  <ArrLoadingSpinner size={18} />
                </Box>
                <Box className="generatingArtifacts text-headings warning-message-text mx-10">
                  {t('ai.review.protocolIntelligence.generatingArtifacts')}
                </Box>
              </Stack>
            )}
            {(isApprovalPending || isReviewedAndApproved) && (
              <Stack direction={'row'}>
                <Box className="mr-10">
                  <Typography
                    className={
                      isApprovalPending ? 'text-inactive' : 'text-subtle'
                    }
                    data-testid={`generate-artifacts-active`}
                  >
                    {t('ai.review.protocolIntelligence.active')}

                    <ArrTooltip
                      content={t(
                        'ai.review.protocolIntelligence.activeTooltip'
                      )}
                      labelId={`tooltip-intelligence-active`}
                    >
                      <InfoIcon
                        className={
                          'isReviewedAndApproved-info-icon ml-4 pos-2' +
                          (isApproved ? ' isApproved-info-icon' : '')
                        }
                        fontSize="small"
                        aria-label={t(
                          'ai.review.protocolIntelligence.activeTooltip'
                        )}
                        data-testid="generate-artifacts-isApprovalPending-isReviewedAndApproved-info-icon"
                      />
                    </ArrTooltip>
                  </Typography>
                </Box>
                <ArrToggle
                  toggleSize="large"
                  disabled={isApprovalPending && !isApproved}
                  checked={isProtocolActive}
                  onChange={handleActiveChange}
                  className="mi-active-toggle"
                  dataTestId="protocol-intelligence-toggle"
                />
              </Stack>
            )}
          </Box>
        </Stack>
        {isFileSubmittedAndArtifactsNotGenerated && (
          <Box className="warning-banner text-headings surface-info-bg">
            <InfoIcon
              className="isFileSubmittedAndArtifactsNotGeneratedMessage-info-icon"
              aria-label={t(
                'ai.review.protocolIntelligence.isFileSubmittedAndArtifactsNotGeneratedMessage'
              )}
              data-testid="generate-artifacts-isFileSubmittedAndArtifactsNotGeneratedMessage-info-icon"
              tabIndex={0}
            />

            <Typography
              className="warning-message-text pl-16"
              data-testid={`generate-artifacts-is-file-submitted-and-artifacts-not-generated-message`}
            >
              {t(
                'ai.review.protocolIntelligence.isFileSubmittedAndArtifactsNotGeneratedMessage'
              )}
            </Typography>
          </Box>
        )}
        {isApprovalPending && (
          <Stack
            direction={'row'}
            justifyContent={'space-between'}
            className="surface-warning-bg warning-banner pr-32"
          >
            <Box className="text-warning">
              <InfoIcon
                className="mr-10"
                aria-label={t(
                  'ai.review.protocolIntelligence.isApprovalPending'
                )}
                data-testid="generate-artifacts-isApprovalPending-info-icon"
                tabIndex={0}
              />
              <Typography
                aria-label={t(
                  'ai.review.protocolIntelligence.isApprovalPending'
                )}
                tabIndex={0}
                className="warning-message-text "
                data-testid={`generate-artifacts-is-approval-pending`}
              >
                {t('ai.review.protocolIntelligence.isApprovalPending')}
              </Typography>
            </Box>
            <Box
              className="text-on-action cursor-pointer"
              onClick={handleReviewArtifacts}
              aria-label={t('ai.review.protocolIntelligence.reviewArtifacts')}
              tabIndex={0}
              data-testid="generate-artifacts-review-artifacts"
            >
              {t('ai.review.protocolIntelligence.reviewArtifacts')} &gt;
            </Box>
          </Stack>
        )}
      </Stack>
      <GenerateArtifactsModal
        open={showGenerateArtifactsModal}
        onClose={handleArtifactsGenerateModalClose}
        submitFileToGenerateArtifacts={submitFileToGenerateArtifacts}
      />
    </>
  );
};

export default GenerateArtifacts;
